import { z } from 'zod'

import { validateAdUnitSizes } from 'features/globalSetup/features/viewport'
import {
  desktopRefreshInterval,
  mobileRefreshInterval,
} from 'features/inventory/domain/forms/RefresherForm/schema'
import { zPercentage } from 'utils/zod'
import { AdUnitForForm } from '../GeneralForm/hooks/useGetAdUnit'
import { mediaTypeRefreshersToSchema } from './schemaMappers'
import { validateRefreshRedirectSchema } from './validator'

export const mediaTypeRefreshersKindValues = ['default', 'viewports'] as const

export type MediaTypeRefreshersKind = (typeof mediaTypeRefreshersKindValues)[number]

const basicSettingsSchema = z.object({
  customizeRefresherBasicSettingsEnabled: z.boolean().default(false),
  desktopAdThreshold: zPercentage.default(80),
  desktopMonitorUserScrollEnabled: z.boolean().default(false),
  desktopRefreshInterval,
  mobileAdThreshold: zPercentage.default(80),
  mobileMonitorUserScrollEnabled: z.boolean().default(false),
  mobileRefreshInterval,
})

const mediaTypeRefresherAttributeSchema = z.object({
  additionalSizes: z.string().array().superRefine(validateAdUnitSizes),
  id: z.string().optional(),
  removeSizes: z.string().array().superRefine(validateAdUnitSizes),
  updatedAt: z.date().optional(),
  viewportId: z.string().optional(),
})

const mediaTypeRefresherViewportSliceSchema = z.object({
  minimumWidth: z.number(),
  name: z.string(),
  viewportId: z.string(),
})

const mediaTypeRefresherViewportAttributeSchema = mediaTypeRefresherAttributeSchema.extend({
  updatedAt: z.date(),
  viewport: mediaTypeRefresherViewportSliceSchema,
  viewportId: z.string(),
})

export const mediaTypeRefreshersSchema = z.object({
  defaultAttribute: mediaTypeRefresherAttributeSchema,
  kind: z.enum(mediaTypeRefreshersKindValues).default('default'),
  newViewportId: z.string().optional(),
  viewportAttributes: mediaTypeRefresherViewportAttributeSchema.array(),
})

const refreshRedirectSchema = z.object({
  mediaTypeRefreshers: mediaTypeRefreshersSchema,
  sendRefreshRequestToExternalAdUnitEnabled: z.boolean().default(false),
  targetAdUnitPath: z.string().optional(),
})

export type MediaTypeRefresherAttributesSchema = z.infer<typeof mediaTypeRefresherAttributeSchema>
export type MediaTypeRefreshersSchema = z.infer<typeof mediaTypeRefreshersSchema>
export type MediaTypeRefresherViewportAttributeSchema = z.infer<
  typeof mediaTypeRefresherViewportAttributeSchema
>
export type MediaTypeRefresherViewportSliceSchema = z.infer<
  typeof mediaTypeRefresherViewportSliceSchema
>
export type RefreshRedirectSchema = z.infer<typeof refreshRedirectSchema>

export const schema = z
  .object({
    basicSettings: basicSettingsSchema,
    id: z.string(),
    refresherEnabled: z.boolean(),
    refreshRedirect: refreshRedirectSchema,
  })
  .superRefine(({ refreshRedirect }, ctx): void => {
    validateRefreshRedirectSchema(refreshRedirect, ctx)
  })

export type Schema = z.infer<typeof schema>

export const schemaDefault = ({ adUnitRefresher, id, refresherEnabled }: AdUnitForForm): Schema => {
  return {
    basicSettings: {
      customizeRefresherBasicSettingsEnabled:
        adUnitRefresher.customizeRefresherBasicSettingsEnabled,
      desktopAdThreshold: adUnitRefresher.desktopAdThreshold,
      desktopMonitorUserScrollEnabled: adUnitRefresher.desktopMonitorUserScrollEnabled,
      desktopRefreshInterval: adUnitRefresher.desktopRefreshInterval,
      mobileAdThreshold: adUnitRefresher.mobileAdThreshold,
      mobileMonitorUserScrollEnabled: adUnitRefresher.mobileMonitorUserScrollEnabled,
      mobileRefreshInterval: adUnitRefresher.mobileRefreshInterval,
    },
    id,
    refresherEnabled,
    refreshRedirect: {
      mediaTypeRefreshers: mediaTypeRefreshersToSchema(adUnitRefresher.mediaTypeRefreshers),
      sendRefreshRequestToExternalAdUnitEnabled:
        adUnitRefresher?.sendRefreshRequestToExternalAdUnitEnabled ?? false,
      targetAdUnitPath: adUnitRefresher?.targetAdUnitPath,
    },
  }
}
